import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'

import Head from 'next/head'
import { useSearchParams } from 'next/navigation'

import { validate as isValidUUID } from 'uuid'

import { CompareOfferCashAdvance } from '@ui/components/Content'
import { AdvertiserDisclosure } from '@ui/components/Disclosure'
import { COMPARE_NAV_LINKS } from '@ui/constants'
import { MarketingLayout } from '@ui/layout/MarketingLayout'

import { usePartnerOffersPlacementPartnerLinks } from '@website/hooks/graphql/useOffersForPlacement'
import { useEventTracking } from '@website/hooks/useEventTracking'
import { Offer } from '@website/types'

export default function CompareCashAdvance() {
  const DASHBOARD_PLACEMENT_INSTANT_CASH =
    process.env.NEXT_PUBLIC_DASHBOARD_PLACEMENT_INSTANT_CASH || ''

  const searchParams = useSearchParams()
  const uid = searchParams?.get('uid') ?? null
  const userId = uid && isValidUUID(uid) ? uid : null

  const { trackEvent } = useEventTracking()
  const [offers, setOffers] = useState<Offer[]>([
    // {
    //   offer: 'compare-cash-advance-atm',
    //   title: 'ATM',
    //   url: '/go?source=%2Fcompare%2Fcash-advance&offer_id=atm',
    // },
    {
      offer: 'compare-cash-advance-klover',
      title: 'Klover',
      url: '/go?source=%2Fcompare%2Fcash-advance&offer_id=klover',
    },
    {
      offer: 'compare-cash-advance-brigit',
      title: 'Brigit',
      url: '/go?source=%2Fcompare%2Fcash-advance&offer_id=brigit',
    },
    // {
    //   offer: 'compare-cash-advance-current',
    //   title: 'Current',
    //   url: '/go?source=%2Fcompare%2Fcash-advance&offer_id=current',
    // },
    // {
    //   offer: 'compare-cash-advance-wisely',
    //   title: 'Wisely by ADP',
    //   url: '/go?source=%2Fcompare%2Fcash-advance&offer_id=wisely',
    // },
  ])
  const { offers: trackingLinks, loading } =
    usePartnerOffersPlacementPartnerLinks(
      DASHBOARD_PLACEMENT_INSTANT_CASH,
      userId ?? undefined,
    )

  useEffect(() => {
    if (!loading && trackingLinks?.length) {
      setOffers((currentOffers) =>
        currentOffers.map((offer) => {
          const matchingLink = trackingLinks.find(
            (link) => link.partner?.name === offer.title,
          )
          return matchingLink
            ? { ...offer, url: matchingLink.ctaUrl || offer.url }
            : offer
        }),
      )
    }
  }, [loading, trackingLinks?.length])

  useEffect(() => {
    trackEvent('view_compare_cash_advance')
  }, [])

  return (
    <>
      <Head>
        <title>Compare Cash Advance Offers - ATM.com</title>
      </Head>
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:mt-2 md:flex-row">
          <div className="flex flex-col gap-4">
            <h1 className="font-display text-4xl leading-tight tracking-tight text-[#0a2d4a] md:text-5xl">
              Compare Cash Advance Offers
            </h1>
            <p className="text-[19px] leading-[1.4] text-[#0a2d4a]">
              Explore cash advance and earned wage access offers to access your
              paycheck early, cover emergency expenses, and avoid costly
              overdraft fees without credit checks or interest.
            </p>
          </div>
          <AdvertiserDisclosure className="leading-tight md:flex-none" />
        </div>
        <div className="mt-6 flex flex-col gap-6">
          {offers.map((offer) => (
            <CompareOfferCashAdvance
              key={offer.offer}
              partnerName={offer.offer}
              ctaUrl={offer.url}
              trackEvent={trackEvent}
            />
          ))}
        </div>
      </div>
    </>
  )
}

CompareCashAdvance.getLayout = (page: ReactElement) => (
  <MarketingLayout
    bg="light-blue"
    banner={false}
    navLinks={COMPARE_NAV_LINKS}
    showBackButton
  >
    {page}
  </MarketingLayout>
)
